import moment from "moment";
import { Dropdown } from "react-bootstrap";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import { CHART_CUSTOM_RANGE_OPTION, LastTimeSelection, SECONDS_PER_DAY, ONE_HOUR_IN_SECONDS } from "constant";
import {
    convertPrometheusTimeToSeconds,
    convertSecondsToPrometheusTime,
    isHttpSuccess,
} from "utils/functions";
import { isEmpty } from "lodash";
import dashboardService from "service/dashboardService";
import { getAPIError, showErrorAlert } from "utils/alert";
import { useDateTimeFormatStore } from "hooks/useDateTimeFormatStore";

const ChartTimeSelectionDropdown = ({
    lastTimeRangeSelect,
    getStartDateTime,
    getEndDateTime,
    setLastTimeRangeSelect,
    dashboardId,
    panelId,
    getTimeGapObject,
    getUpdatedChartConfig,
    onChange,
}: any) => {
    const { dateTimeFormat, is12HourFormat } = useDateTimeFormatStore((state) => ({
        dateTimeFormat: state.dateTimeFormat,
        is12HourFormat: state.is12HourFormat,
    }));
    
    const getSelectedLabel = () => {
        if (lastTimeRangeSelect.query_time) {
            return CHART_CUSTOM_RANGE_OPTION;
        } else {
            const lastTimeOption: any = LastTimeSelection.find(
                ({ timeGapToNow }) =>
                    timeGapToNow ===
                    convertPrometheusTimeToSeconds(
                        lastTimeRangeSelect.time_range
                    )
            );
            return lastTimeOption?.time;
        }
    };

    const applySelectedTimeRange = async (event: any, picker: any) => {
        const { chosenLabel, endDate, startDate } = picker;
        const endDateTimestamp = endDate.unix();

        const timeValue = Math.round(
            moment.duration(endDate.diff(startDate)).asSeconds()
        );

        // Check if we're switching from a long time range to a short one
        const currentTimeRangeInSeconds = convertPrometheusTimeToSeconds(lastTimeRangeSelect.time_range);
        const isFromLongRange = currentTimeRangeInSeconds >= SECONDS_PER_DAY || lastTimeRangeSelect.query_time > 0;
        const isToShortRange = timeValue <= ONE_HOUR_IN_SECONDS && chosenLabel !== CHART_CUSTOM_RANGE_OPTION;
        const shouldSetAutoRefresh = isFromLongRange && isToShortRange;

        const updatePayload: any = {
            time_range: convertSecondsToPrometheusTime(timeValue),
            query_time:
                chosenLabel === CHART_CUSTOM_RANGE_OPTION
                    ? endDateTimestamp
                    : 0,
        };

        // Set refresh rate to "auto" when switching from long range to short range
        if (shouldSetAutoRefresh) {
            updatePayload.refresh_rate = "auto";
        }

        const panelRes = await dashboardService.updatePanel(
            dashboardId,
            panelId,
            updatePayload
        );
        if (!isHttpSuccess(panelRes?.status)) {
            showErrorAlert(
                getAPIError(
                    panelRes,
                    "Something went wrong when changing the time range. Please try again."
                )
            );
            return;
        }

        const timeGapObj = getTimeGapObject(timeValue);

        const newLastTimeRangeSelect: any = {
            ...lastTimeRangeSelect,
        };

        // This function helps to add in INTVL, showAggregated, showCandlestickChart
        if (!isEmpty(timeGapObj)) {
            const _newLastTimeRangeSelect = await getUpdatedChartConfig(
                timeValue
            );

            Object.keys(_newLastTimeRangeSelect).forEach((k) => {
                newLastTimeRangeSelect[k] = _newLastTimeRangeSelect[k];
            });
        }

        // Batch state updates to prevent cascading re-renders
        const finalLastTimeRangeSelect = {
            ...newLastTimeRangeSelect,
            time_range: convertSecondsToPrometheusTime(timeValue),
            query_time:
                chosenLabel === CHART_CUSTOM_RANGE_OPTION
                    ? endDateTimestamp
                    : 0,
        };

        // Update local state
        setLastTimeRangeSelect(finalLastTimeRangeSelect);
        
        // Notify parent component about the change
        if (onChange) {
            onChange({
                timeRangeSelection: finalLastTimeRangeSelect,
                shouldSetAutoRefresh,
            });
        }
    };

    return (
        <Dropdown alignRight>
            <DateRangePicker
                initialSettings={{
                    timePicker: true,
                    timePicker24Hour: !is12HourFormat,
                    startDate: getStartDateTime(),
                    endDate: getEndDateTime(),
                    maxDate: moment(),
                    locale: {
                        format: dateTimeFormat,
                    },
                    maxSpan: {
                        years: 3,
                    },
                    ranges: LastTimeSelection.reduce<any>(
                        (acc, timeSelection) => {
                            const { time, timeGapToNow } = timeSelection;
                            acc[`${time}`] = [
                                moment().subtract(timeGapToNow, "seconds"),
                                moment(),
                            ];
                            return acc;
                        },
                        {}
                    ),
                }}
                onApply={applySelectedTimeRange}
            >
                <Dropdown.Toggle variant="" className="last-data">
                    {getSelectedLabel()}
                </Dropdown.Toggle>
            </DateRangePicker>
        </Dropdown>
    );
};

export default ChartTimeSelectionDropdown;
